import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { MoneyText } from '@/components/ui/MoneyText'
import { Sheet } from '@/components/ui/Sheet'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Textarea } from '@/components/ui/Textarea'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { canWriteDepartment } from '@/auth/profile'
import { useProfile } from '@/auth/SessionProvider'
import { memberLedger } from '@/data/dues'
import type { ChargeLine } from '@/data/dues'
import { listDepartmentMembers, removeMemberFromDepartment, updateMember } from '@/data/members'
import type { DepartmentMember } from '@/data/members'
import { formatZambianPhone, normalizeZambianPhone } from '@/lib/phone'
import { lusakaToday } from '@/lib/periods'
import { editMemberSchema } from './schemas'
import type { EditMemberValues } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** 'YYYY-MM-DD' -> 'July 2026' without going through Date (no timezone shifts). */
function monthName(date: string): string {
  const [y, m] = date.split('-').map(Number)
  return `${MONTHS[(m ?? 1) - 1] ?? ''} ${y}`
}

/** 'YYYY-MM-DD' -> '5 July 2026'. */
function longDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return `${d} ${MONTHS[(m ?? 1) - 1] ?? ''} ${y}`
}

function StatusBadge({ c }: { c: ChargeLine }) {
  if (c.status === 'settled') return <Badge tone="success">Paid</Badge>
  if (c.status === 'waived') return <Badge tone="neutral">Waived</Badge>
  if (c.status === 'cancelled') return <Badge tone="neutral">Cancelled</Badge>
  if (c.paidNgwee > 0) return <Badge tone="warning">Part paid</Badge>
  if (c.daysOverdue > 0) return <Badge tone="danger">Overdue</Badge>
  return <Badge tone="neutral">Due soon</Badge>
}

function EditForm({ deptId, member, onDone }: { deptId: string; member: DepartmentMember; onDone: () => void }) {
  const qc = useQueryClient()
  const busy = useRef(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditMemberValues>({
    resolver: zodResolver(editMemberSchema),
    defaultValues: {
      fullName: member.fullName,
      phone: member.phone ? formatZambianPhone(member.phone) : '',
      email: member.email ?? '',
      notes: member.notes ?? '',
    },
  })
  const save = useMutation({
    mutationFn: (v: EditMemberValues) =>
      updateMember(member.memberId, {
        fullName: v.fullName,
        phone: normalizeZambianPhone(v.phone),
        email: v.email.trim() || null,
        notes: v.notes.trim() || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['members', deptId] })
      onDone()
    },
    onSettled: () => {
      busy.current = false
    },
  })
  const submit = handleSubmit((v) => {
    if (busy.current) return
    busy.current = true
    save.mutate(v)
  })
  return (
    <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
      <Input label="Full name" autoComplete="off" error={errors.fullName?.message} {...register('fullName')} />
      <Input
        label="Phone (recommended)"
        type="tel"
        inputMode="tel"
        autoComplete="off"
        hint="Used to avoid adding the same person twice"
        error={errors.phone?.message}
        {...register('phone')}
      />
      <Input
        label="Email (optional)"
        type="email"
        inputMode="email"
        autoComplete="off"
        error={errors.email?.message}
        {...register('email')}
      />
      <Textarea
        label="Notes (optional)"
        rows={3}
        hint="Anyone who can see this member can read the note."
        {...register('notes')}
      />
      {save.isError && (
        <p role="alert" className="text-body text-bad">
          {errText(save.error)}
        </p>
      )}
      <Button type="submit" size="lg" isLoading={save.isPending}>
        {save.isPending ? 'Saving...' : 'Save changes'}
      </Button>
    </form>
  )
}

function RemoveConfirm({
  deptId,
  member,
  onCancel,
}: {
  deptId: string
  member: DepartmentMember
  onCancel: () => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const busy = useRef(false)
  const remove = useMutation({
    mutationFn: () => removeMemberFromDepartment(member.membershipId, deptId, lusakaToday()),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['members', deptId] })
      await navigate({ to: '/d/$deptId/members', params: { deptId } })
    },
    onSettled: () => {
      busy.current = false
    },
  })
  return (
    <div className="flex flex-col gap-4">
      <p className="text-neutral">
        Remove {member.fullName} from your department? They will stop being charged dues. What they already owe
        stays on record.
      </p>
      {remove.isError && (
        <p role="alert" className="text-body text-bad">
          {errText(remove.error)}
        </p>
      )}
      <Button
        variant="danger"
        size="lg"
        isLoading={remove.isPending}
        onClick={() => {
          if (busy.current) return
          busy.current = true
          remove.mutate()
        }}
      >
        {remove.isPending ? 'Removing...' : 'Yes, remove them'}
      </Button>
      <Button variant="secondary" size="lg" onClick={onCancel} disabled={remove.isPending}>
        Cancel
      </Button>
    </div>
  )
}

export function MemberDetailPage({ deptId, memberId }: { deptId: string; memberId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const [editOpen, setEditOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  const navigateAway = useNavigate()

  const members = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })
  const ledger = useQuery({ queryKey: ['ledger', deptId, memberId], queryFn: () => memberLedger(deptId, memberId) })

  const back = (
    <Link
      to="/d/$deptId/members"
      params={{ deptId }}
      className="inline-flex min-h-[44px] items-center text-body font-medium text-brand hover:underline"
    >
      Back to members
    </Link>
  )

  if (members.isPending) {
    return (
      <SkeletonRows rows={4} />
    )
  }
  if (members.isError) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <Card>
          <CardBody>
            <p role="alert" className="text-body text-bad">
              {errText(members.error)}
            </p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void members.refetch()}>
              Try again
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  // A person may have more than one membership over time; prefer the current one.
  const mine = members.data.filter((m) => m.memberId === memberId)
  const member = mine.find((m) => m.leftOn === null) ?? mine[0]

  if (!member) {
    return (
      <div className="flex flex-col gap-3">
        {back}
        <Card>
          <CardBody>
            <h2 className="text-section font-semibold text-ink">We could not find that member</h2>
            <p className="mt-1 text-body text-muted">
              The link may be old, or this person is not in your department.
            </p>
          </CardBody>
        </Card>
      </div>
    )
  }

  const leftOn = member.leftOn
  const hasLeft = leftOn !== null

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      {back}
      <div>
        <h2 className="text-title font-semibold text-ink">{member.fullName}</h2>
        {leftOn !== null && <p className="mt-1 text-body text-muted">Left on {longDate(leftOn)}</p>}
      </div>

      <Card>
        <CardBody>
          <dl className="grid grid-cols-[6rem_1fr] items-center gap-y-3 text-body">
            <dt className="text-muted">Phone</dt>
            <dd className="text-ink">
              {member.phone ? (
                <a
                  href={`tel:${member.phone}`}
                  className="inline-flex min-h-[44px] items-center text-brand hover:underline"
                >
                  {formatZambianPhone(member.phone)}
                </a>
              ) : (
                'Not given'
              )}
            </dd>
            <dt className="text-muted">Email</dt>
            <dd className="break-all text-ink">{member.email ?? 'Not given'}</dd>
            <dt className="text-muted">Joined</dt>
            <dd className="text-ink">{longDate(member.joinedOn)}</dd>
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {member.arrearsNgwee > 0 ? (
            <div>
              <p className="text-body text-muted">Owes</p>
              <p className="text-title font-semibold text-wait">
                <MoneyText ngwee={member.arrearsNgwee} />
              </p>
              <p className="mt-1 text-body text-muted">
                Behind by {member.periodsBehind === 1 ? '1 month' : `${member.periodsBehind} months`}
                {member.oldestUnpaidPeriod ? `, since ${monthName(member.oldestUnpaidPeriod)}` : ''}.
              </p>
            </div>
          ) : (
            <p className="text-title font-semibold text-ok">Up to date</p>
          )}
        </CardBody>
      </Card>

      {canWrite && (
        <div className="flex flex-col gap-3 sm:flex-row">
          {!hasLeft && (
            <Link
              to="/d/$deptId/pay"
              params={{ deptId }}
              search={{ member: memberId }}
              className="inline-flex min-h-[44px] items-center justify-center rounded-control bg-brand px-6 py-3 font-medium text-white hover:bg-brand-hover"
            >
              Record payment
            </Link>
          )}
          <Button variant="secondary" size="lg" onClick={() => setEditOpen(true)}>
            Edit details
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <h3 className="font-semibold text-ink">Payment history</h3>
        </CardHeader>
        {ledger.isPending ? (
          <SkeletonRows rows={4} />
        ) : ledger.isError ? (
          <CardBody>
            <p role="alert" className="text-body text-bad">
              {errText(ledger.error)}
            </p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void ledger.refetch()}>
              Try again
            </Button>
          </CardBody>
        ) : ledger.data.length === 0 ? (
          <CardBody>
            <p className="text-body text-muted">No dues have been charged to this person yet.</p>
          </CardBody>
        ) : (
          <ul className="divide-y divide-line">
            {[...ledger.data]
              .sort((a, b) => (a.periodStart < b.periodStart ? 1 : a.periodStart > b.periodStart ? -1 : 0))
              .map((c) => (
                <li key={c.chargeId} className="flex items-center justify-between gap-3 px-6 py-3">
                  <div>
                    <p className="font-medium text-ink">{monthName(c.periodStart)}</p>
                    <p className="text-body text-muted">
                      Due <MoneyText ngwee={c.amountNgwee} /> · Paid <MoneyText ngwee={c.paidNgwee} />
                    </p>
                  </div>
                  <StatusBadge c={c} />
                </li>
              ))}
          </ul>
        )}
      </Card>

      {canWrite && !hasLeft && (
        <div>
          <Button variant="ghost" size="lg" className="text-bad" onClick={() => setRemoveOpen(true)}>
            Remove from department
          </Button>
        </div>
      )}

      <OwnerDeleteButton
        table="member"
        id={memberId}
        what="member"
        wrap="flex flex-wrap gap-3"
        onDeleted={() => void navigateAway({ to: '/d/$deptId/members', params: { deptId } })}
      />

      {canWrite && (
        <>
          <Sheet isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit details">
            <EditForm deptId={deptId} member={member} onDone={() => setEditOpen(false)} />
          </Sheet>
          <Sheet isOpen={removeOpen} onClose={() => setRemoveOpen(false)} title="Remove from department">
            <RemoveConfirm deptId={deptId} member={member} onCancel={() => setRemoveOpen(false)} />
          </Sheet>
        </>
      )}
    </div>
  )
}
